import { useState, type MouseEvent } from 'react';
import { IconButton, Tooltip, useToast } from '@chakra-ui/react';
import { t } from '@lingui/core/macro';
import { ExternalLink } from 'lucide-react';
import { getTenantUrl } from '@duke-hq/lib';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { useAdminTestClient } from '@duke-hq/app-admin-web/lib/adminTestClient';
import { testIds } from './testIds';

type LoginAsButtonProps =
  | { kind: 'student'; studentId: string }
  | { kind: 'consultant'; consultantId: string; providerId: number };

export function LoginAsButton(props: LoginAsButtonProps) {
  // The admin test-server is only reachable through the local Vite dev-server proxy.
  if (import.meta.env.MODE !== 'development') {
    return null;
  }

  return <LoginAsIconButton {...props} />;
}

function LoginAsIconButton(props: LoginAsButtonProps) {
  const testClient = useAdminTestClient();
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const label =
    props.kind === 'student' ? t`Log in to the student app` : t`Log in to the provider app`;

  async function getLoginUrl() {
    if (props.kind === 'student') {
      const { token } = await testClient.loginLinks.student.mutate({
        studentId: props.studentId,
      });
      return `${getTenantUrl('app')}/magiclink?token=${token}`;
    }

    const { token, subdomain } = await testClient.loginLinks.consultant.mutate({
      consultantId: props.consultantId,
      providerId: props.providerId,
    });
    return `${getTenantUrl(subdomain)}/magiclink?token=${token}`;
  }

  async function handleClick(event: MouseEvent) {
    // Names often sit inside clickable rows and links.
    event.preventDefault();
    event.stopPropagation();

    // Open the tab before awaiting so the popup blocker treats it as user-initiated.
    const tab = window.open('', '_blank');
    setIsLoading(true);

    try {
      const loginUrl = await getLoginUrl();
      if (tab != null) {
        tab.location.href = loginUrl;
      }
    } catch {
      tab?.close();
      toast({ status: 'error', description: t`Could not create a login link.` });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Tooltip label={label}>
      <IconButton
        data-testid={testIds.button}
        aria-label={label}
        icon={<ExternalLink size={iconSize.sm} />}
        size="xs"
        variant="ghost"
        isLoading={isLoading}
        onClick={handleClick}
      />
    </Tooltip>
  );
}
